import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import LogRocket from 'logrocket';
import * as Sentry from "@sentry/react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Generate cloudinary thumbnail URL
export const getThumbnailUrl = (originalUrl: string, width: number = 80, height: number = 80) => {
  if (!originalUrl) return '';

  // For cloudinary URLs, add transformation parameters
  if (originalUrl.includes('cloudinary.com')) {
    // Insert transformation before the image path
    const parts = originalUrl.split('/upload/');
    if (parts.length === 2) {
      return `${parts[0]}/upload/w_${width},h_${height},c_fill,g_center/${parts[1]}`;
    }
  }

  return originalUrl;
};

export const getCurrentAlbumSlug = () => {
  // Safely get album slug from current URL path
  try {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }
    const pathSegments = window.location.pathname.split('/').filter(Boolean);
    return pathSegments.length > 0 ? pathSegments[0] : null;
  } catch (error) {
    console.warn('Error getting album slug:', error);
    return null;
  }
}


// Optimize cloudinary URL for different contexts
export const optimizeCloudinaryUrl = ({
  originalUrl,
  width,
  height,
  isModal,
  maxWidth,
}: {
  originalUrl: string
  width: number
  height?: number
  isModal?: boolean
  maxWidth?: number
}) => {
  if (!originalUrl || typeof originalUrl !== 'string') return originalUrl

  try {
    if (originalUrl.includes('cloudinary.com')) {
      const parts = originalUrl.split('/upload/')
      if (parts.length === 2) {
        if (isModal) {
          const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768
          const modalMaxWidth = isMobile ? 1600 : Math.min((width || 1200) * 2, 2400)
          const modalMaxHeight = isMobile ? 1200 : Math.min((height || 900) * 2, 1800)
          return `${parts[0]}/upload/w_${modalMaxWidth},h_${modalMaxHeight},c_fit,f_auto,q_auto:good,dpr_auto/${parts[1]}`
        } else {
          const effectiveWidth = maxWidth ?? width
          const w = effectiveWidth ? `w_${effectiveWidth}` : ''
          const h = maxWidth ? '' : height ? `,h_${height}` : ''
          const transform = [w, h, 'c_fit', 'f_auto', 'q_auto'].filter(Boolean).join(',')
          return `${parts[0]}/upload/${transform}/${parts[1]}`
        }
      }
    }
  } catch (e) {
    console.error("optimizeCloudinaryUrl error:", e)
  }

  return originalUrl
}

export const isAppRoot = (location: { pathname?: string }) => {
  const path = location?.pathname ?? "/";
  const segments = path.split("/").filter(Boolean);
  return segments.length === 0;
};

export const isPanelPage = (location: { pathname?: string }) => {
  const path = location?.pathname ?? "/";
  const segments = path.split("/").filter(Boolean);
  return segments[0] === "panel";
};


export function safeLogRocketTrack(event: string, props?: Record<string, any>) {
  if (import.meta.env.PROD && typeof LogRocket?.track === 'function') {
    try {
      LogRocket.track(event, props);
    } catch (err) {
      console.warn('[LogRocket] track failed:', err);
    }
  }
}
export function safeLogRocketIdentify(userID: string, props?: Record<string, any>) {
  if (import.meta.env.PROD && typeof LogRocket?.identify === 'function') {
    try {
      LogRocket.identify(userID, props);
    } catch (err) {
      console.warn('[LogRocket] identify failed:', err);
    }
  }
}

// Determines if a user is considered "active" based on last activity timestamp
export const isUserActive = (lastActiveAt?: string): boolean => {
  if (!lastActiveAt) return false;
  const lastActiveTime = new Date(lastActiveAt).getTime();
  const now = Date.now();
  const thirtySecondsAgo = now - (30 * 1000);
  return lastActiveTime > thirtySecondsAgo;
};

// Formats last login using device's local timezone
export function formatLastLogin(dateString: string) {
  try {
    const date = new Date(dateString);
    const now = new Date();
    
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffTime = startOf(now).getTime() - startOf(date).getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    const timeStr = new Intl.DateTimeFormat('es-ES', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);

    if (diffDays === 0) {
      return `Hoy a las ${timeStr}`;
    } else if (diffDays === 1) {
      return `Ayer a las ${timeStr}`;
    } else if (diffDays === 2) {
      return `Anteayer a las ${timeStr}`;
    } else {
      const day = new Intl.DateTimeFormat('es-ES', { day: 'numeric' }).format(date);
      const month = new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(date);
      return `${day} de ${month} a las ${timeStr}`;
    }
  } catch (error) {
    return 'No disponible';
  }
}

// Formats upload time using the user's local time zone and the provided locale for day/month names
export function formatUploadTime(dateString: string, locale?: string): string {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return locale?.toLowerCase().startsWith('en') ? 'Not available' : 'Información no disponible';

    const now = new Date();
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.floor((startOf(now).getTime() - startOf(date).getTime()) / (1000 * 60 * 60 * 24));

    const resolvedLocale = locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
    const langRoot = resolvedLocale.split('-')[0].toLowerCase();

    const timeStr = new Intl.DateTimeFormat(resolvedLocale, {
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);

    const day = new Intl.DateTimeFormat(resolvedLocale, { day: 'numeric' }).format(date);
    const month = new Intl.DateTimeFormat(resolvedLocale, { month: 'long' }).format(date);

    if (langRoot === 'en') {
      if (diffDays === 0) return `Uploaded at ${timeStr}`;
      if (diffDays === 1) return `Uploaded yesterday at ${timeStr}`;
      // For English, do not use "day before yesterday" — fall back to absolute date
      return `Uploaded: ${day} de ${month} at ${timeStr}`;
    }

    // Default (Spanish variants)
    if (diffDays === 0) return `Subido a las ${timeStr}`;
    if (diffDays === 1) return `Subido ayer, a las ${timeStr}`;
    if (diffDays === 2) return `Subido antes de ayer, a las ${timeStr}`;
    return `Subido el ${day} de ${month} a las ${timeStr}`;
  } catch (error) {
    return locale?.toLowerCase().startsWith('en') ? 'Not available' : 'Información no disponible';
  }
}

// Netlify Functions root resolver
export function getNetlifyFunctionsRoot(): string {
  return import.meta.env.DEV ? "http://localhost:9999/.netlify/functions" : "/.netlify/functions";
}

// Centralized helper to delete a media by ID via Netlify Function
export async function deleteMediaById(id: string): Promise<void> {
  const url = `${getNetlifyFunctionsRoot()}/delete-media?id=${encodeURIComponent(id)}`;
  const resp = await fetch(url, { method: 'DELETE' });
  if (!resp.ok) {
    let text = '';
    try { text = await resp.text(); } catch { }
    throw new Error(text || `Failed to delete media ${id}`);
  }
  // best-effort parse, don't break if body empty
  try { await resp.json(); } catch { }
}

// Download image function with iOS/Android/Desktop behavior
export const downloadImage = async ({ imageUrl, albumSlug, fileName, userId, toast }: { imageUrl: string, fileName?: string, albumSlug: string, userId: string, toast: any }) => {
  try {
    safeLogRocketTrack("Click en Descargar Foto", {
      user_id: userId,
      album_name: albumSlug || '',
    });

    // Fetch image as blob
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    // Re-encodear a JPEG usando Canvas
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    ctx?.drawImage(bitmap, 0, 0);

    const jpegBlob: Blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b!), "image/jpeg", 0.92); // calidad 92%
    });

    const file = new File(
      [jpegBlob],
      fileName || `${albumSlug.toUpperCase()}-foto-${Date.now()}.jpg`,
      { type: "image/jpeg" }
    );

    // Detect iOS and Android
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);

    if (
      isIOS &&
      navigator.canShare &&
      navigator.canShare({ files: [file] })
    ) {
      // iOS: use Web Share API (native sheet, user can save to Photos)
      await navigator.share({
        files: [file],
        title: "Foto del evento",
      });
      return;
    }
    // On Android, skip navigator.share (force download as file).
    // On desktop, also fallback to download.

    // Download: create object URL and trigger download
    const url = window.URL.createObjectURL(jpegBlob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    if (isAndroid) {
      toast({
        title: "Foto guardada",
        description: "La imagen se guardó en Descargas.",
      });
    } else {
      toast({
        title: "Descarga iniciada",
        description: "La imagen se está descargando.",
      });
    }

    safeLogRocketTrack("Descarga de Foto Completa", {
      user_id: userId,
      album_name: albumSlug || '',
    });
  } catch (error) {
    console.error("Error downloading image:", error);
    Sentry.captureMessage(
      error instanceof Error ? error.message : String(error),
      'error'
    );
    safeLogRocketTrack("Error en Descarga de Foto", {
      user_id: userId,
      album_name: albumSlug || '',
    });

    // Last resort for iOS Safari: open in new tab with Cloudinary fl_attachment
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      let downloadUrl = imageUrl;
      if (imageUrl.includes("res.cloudinary.com")) {
        const parts = imageUrl.split("/upload/");
        if (parts.length === 2) {
          downloadUrl = `${parts[0]}/upload/fl_attachment/${parts[1]}`;
        }
      }
      window.open(downloadUrl, "_blank");
      return;
    }

    toast({
      title: "Error de descarga",
      description: "No se pudo descargar la imagen.",
      variant: "destructive",
    });
  }
};
